import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const pour = keyframes`
  0% { transform: rotate(0deg); }
  50% { transform: rotate(-45deg); }
  100% { transform: rotate(-15deg); }
`;

const splash = keyframes`
  0% { transform: scale(1) rotate(0deg); }
  50% { transform: scale(1.3) rotate(360deg); opacity: 0.7; }
  100% { transform: scale(1) rotate(720deg); opacity: 1; }
`;

const WineSectionContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  margin: 30px auto;
  max-width: 1200px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const WineTitle = styled.h2`
  font-size: 2.5em;
  color: #fff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  margin-bottom: 10px;
`;

const WineSubtitle = styled.p`
  font-size: 1.2em;
  color: #ffe066;
  margin-bottom: 30px;
`;

const WineContainer = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 40px;
  padding: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const WineStation = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const WineBottle = styled.div`
  position: relative;
  width: 80px;
  height: 200px;
  cursor: pointer;
  transition: transform 0.3s;
  filter: drop-shadow(0 5px 15px rgba(0, 0, 0, 0.3));

  &:hover {
    transform: scale(1.1) rotate(-5deg);
  }

  &:active {
    transform: scale(0.95) rotate(-15deg);
    animation: ${pour} 0.6s ease-out;
  }
`;

const BottleBody = styled.div`
  position: absolute;
  bottom: 0;
  left: 10px;
  width: 60px;
  height: 140px;
  border-radius: 10px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1));

  &::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 5px;
    width: 15px;
    height: 60px;
    background: linear-gradient(to right, rgba(255, 255, 255, 0.6), transparent);
    border-radius: 50%;
  }

  &.red-wine { background-color: #8b0000; }
  &.white-wine { background-color: #f4e4c1; }
  &.rose-wine { background-color: #ffb6c1; }
`;

const BottleNeck = styled.div`
  position: absolute;
  bottom: 140px;
  left: 25px;
  width: 30px;
  height: 50px;
  background: linear-gradient(to bottom, rgba(50, 50, 50, 0.8), rgba(30, 30, 30, 0.9));
  border-radius: 5px 5px 0 0;
`;

const BottleCap = styled.div`
  position: absolute;
  bottom: 185px;
  left: 28px;
  width: 24px;
  height: 15px;
  background: linear-gradient(to bottom, gold, #b8860b);
  border-radius: 3px 3px 0 0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
`;

const BottleLabel = styled.div`
  position: absolute;
  bottom: 60px;
  left: 20px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
`;

const FrolkeFace = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s;

  &.wine-splash {
    animation: ${splash} 0.6s ease-out;
  }
`;

const WineGlassContainer = styled.div`
  position: relative;
  width: 100px;
  height: 150px;
  pointer-events: none;
`;

function WineSection() {
  const [pourEffects, setPourEffects] = useState({});

  const wines = [
    { id: 1, type: 'red', emoji: '🍇', name: 'Red Wine' },
    { id: 2, type: 'white', emoji: '🥂', name: 'White Wine' },
    { id: 3, type: 'rose', emoji: '🌹', name: 'Rosé Wine' }
  ];

  const handleBottleClick = (wineId) => {
    // Trigger pour effect
    setPourEffects(prev => ({ ...prev, [wineId]: true }));
    
    // Create wine drops from bottle to face
    const station = document.querySelector(`[data-wine-id="${wineId}"]`);
    const face = document.getElementById(`frolkeFace${wineId}`);
    
    if (station && face) {
      const bottleRect = station.querySelector('div').getBoundingClientRect(); // Get first div (WineBottle)
      const faceRect = face.getBoundingClientRect();
      
      // Start position (top of bottle)
      const startX = bottleRect.left + bottleRect.width / 2;
      const startY = bottleRect.top;
      
      // End position (top of face)
      const endY = faceRect.top;
      
      for (let i = 0; i < 15; i++) {
        setTimeout(() => {
          const drop = document.createElement('div');
          drop.className = 'wine-drop';
          drop.style.position = 'fixed';
          drop.style.left = (startX + (Math.random() * 20 - 10)) + 'px';
          drop.style.top = startY + 'px';
          drop.style.setProperty('--drop-distance', (endY - startY) + 'px');
          document.body.appendChild(drop);
          
          setTimeout(() => drop.remove(), 1500);
        }, i * 50);
      }
    }

    // Create particle explosion - wait for wine to hit face
    setTimeout(() => {
      const face = document.getElementById(`frolkeFace${wineId}`);
      if (face) {
        const rect = face.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        for (let i = 0; i < 30; i++) {
          setTimeout(() => {
            const particle = document.createElement('div');
            particle.className = 'steven-particle';
            particle.style.left = centerX + 'px';
            particle.style.top = centerY + 'px';
            
            const angle = (i / 30) * Math.PI * 2;
            const distance = Math.random() * 200 + 100;
            particle.style.setProperty('--sx', Math.cos(angle) * distance + 'px');
            particle.style.setProperty('--sy', Math.sin(angle) * distance + 'px');
            
            document.body.appendChild(particle);
            
            setTimeout(() => particle.remove(), 2000);
          }, i * 30);
        }
      }
    }, 1200);

    // Reset effects
    setTimeout(() => {
      setPourEffects(prev => ({ ...prev, [wineId]: false }));
    }, 2000);
  };

  return (
    <WineSectionContainer>
      <WineTitle>🍷 STEVEN'S WINE COLLECTION 🍷</WineTitle>
      <WineSubtitle>Click the bottles to celebrate!</WineSubtitle>
      
      <WineContainer>
        {wines.map(wine => (
          <WineStation key={wine.id} data-wine={wine.type} data-wine-id={wine.id}>
            <WineBottle onClick={() => handleBottleClick(wine.id)}>
              <BottleBody className={`${wine.type}-wine`} />
              <BottleNeck />
              <BottleCap />
              <BottleLabel>{wine.emoji}</BottleLabel>
            </WineBottle>
            <FrolkeFace
              src={wine.id % 2 === 0 ? '/frolke2.jpg' : '/frolke.jpg'} 
              className={pourEffects[wine.id] ? 'wine-splash' : ''}
              id={`frolkeFace${wine.id}`}
              alt="Steven Frolke" 
            />
            <WineGlassContainer id={`glassContainer${wine.id}`} />
          </WineStation>
        ))}
      </WineContainer>
    </WineSectionContainer>
  );
}

export default WineSection;
